import { GetOutboundSchedulesRes } from '@/services/outboundSchedule/types'
import { useIsExpiredCutOff } from '../../hooks/useIsExpiredCutOff'
import { CutOffHoverConsumer, CutOffHoverProvider } from '../../context/CutOffHoverContext'
import { HStack, VStack } from '@musinsa/mwms-ds'
import { CELL_WIDTH } from '../OutboundTable'
import Text from '@/components/content/Text'
import styled from 'styled-components'
import { BaseCell } from '../shared/BaseCell'
import { colors, colorSystem } from '@/styles/theme'
import { CutOffCutTypeCell } from './CutOffCutTypeCell'
import { CutOffCell } from './CutOffCell'
import { RefObject } from 'react'
import { TimeSlot } from '../../hooks/useGetTimeSlots'
import { CutOffRowDateTimeProvider } from '../../context/CutOffRowDateTimeContext'
import { useGetCutOffWarehouses } from '@/services/warehouse/useWareHouseQueries'

type Props = {
  data: TimeSlot
  scrollContainerRef: RefObject<HTMLDivElement>
  warehouses: GetOutboundSchedulesRes['warehouses']
}

export function CutOffRow({ data, scrollContainerRef, warehouses }: Props) {
  const { date, time, schedules } = data

  const { data: cutOffWarehousesData } = useGetCutOffWarehouses()
  const mwmsWarehouseCodes = cutOffWarehousesData?.filter((warehouse) => warehouse.module === 'MWMS').map((warehouse) => warehouse.warehouseCode)

  const hasB2cSchedules = !!(
    schedules
      ?.find((schedule) => schedule.cutType === 'B2C')
      ?.centerSchedules.filter((centerSchedule) => mwmsWarehouseCodes?.includes(centerSchedule.warehouseCode))?.length ?? 0
  )

  const hasB2bSchedules = !!(
    schedules
      ?.find((schedule) => schedule.cutType === 'B2B')
      ?.centerSchedules.filter((centerSchedule) => mwmsWarehouseCodes?.includes(centerSchedule.warehouseCode))?.length ?? 0
  )

  const b2cSchedule = schedules?.find((schedule) => schedule.cutType === 'B2C')
  const b2bSchedule = schedules?.find((schedule) => schedule.cutType === 'B2B')

  const isExpired = useIsExpiredCutOff({ date, time })

  return (
    <CutOffRowDateTimeProvider date={date} time={time}>
      <CutOffHoverProvider>
        <CutOffHoverConsumer>
          {({ isCutOffHover, setIsCutOffHover }) => (
            <HStack
              key={time}
              onMouseEnter={() => {
                if (isExpired) return
                setIsCutOffHover(true)
              }}
              onMouseLeave={() => {
                if (isExpired) return
                setIsCutOffHover(false)
              }}
            >
              <CutOffLeftWrap style={{ minWidth: CELL_WIDTH, height: 96 }}>
                <TimeCell $isExpired={isExpired} $isCutOffHover={isCutOffHover}>
                  <Text kind="Body_14_medium" color={isExpired ? colorSystem.text.common.tertiary : colorSystem.text.common.primary}>
                    {time}
                  </Text>
                </TimeCell>
                <VStack style={{ flex: 1 }}>
                  <CutOffCutTypeCell type="B2C" scrollContainerRef={scrollContainerRef} hasSchedules={hasB2cSchedules} schedule={b2cSchedule} />
                  <CutOffCutTypeCell type="B2B" scrollContainerRef={scrollContainerRef} hasSchedules={hasB2bSchedules} schedule={b2bSchedule} />
                </VStack>
              </CutOffLeftWrap>

              {warehouses?.map(({ warehouseCode }) => {
                const b2cSchedule = schedules
                  .find((schedule) => schedule.cutType === 'B2C')
                  ?.centerSchedules.find((schedule) => schedule.warehouseCode === warehouseCode)

                const b2bSchedule = schedules
                  .find((schedule) => schedule.cutType === 'B2B')
                  ?.centerSchedules.find((schedule) => schedule.warehouseCode === warehouseCode)

                return (
                  <VStack style={{ minWidth: CELL_WIDTH }}>
                    {b2cSchedule ? <CutOffCell cutType="B2C" data={b2cSchedule} /> : <NotOperationCell $isCutOffHover={isCutOffHover} />}
                    {b2bSchedule ? <CutOffCell cutType="B2B" data={b2bSchedule} /> : <NotOperationCell $isCutOffHover={isCutOffHover} />}
                  </VStack>
                )
              })}
            </HStack>
          )}
        </CutOffHoverConsumer>
      </CutOffHoverProvider>
    </CutOffRowDateTimeProvider>
  )
}

const CutOffLeftWrap = styled(HStack)`
  background-color: ${colors.white};
  z-index: 9;

  position: sticky;
  left: 0;
`

const TimeCell = styled(BaseCell)<{ $isExpired: boolean; $isCutOffHover: boolean }>`
  width: 120px;
  background-color: ${({ $isCutOffHover, $isExpired }) => ($isCutOffHover ? '#f4faff' : $isExpired ? '#00000008' : 'transparent')};
`

const NotOperationCell = styled(BaseCell)<{ $isCutOffHover: boolean }>`
  flex: 1;
  background-color: ${({ $isCutOffHover }) => ($isCutOffHover ? '#f4faff' : '#00000008')};
`
